import { db } from "./index.js";
import { deleteRouteDatapoint, getRouteDatapoints } from "./routeDatapoints.js";
import { deleteRouteLocation, getRouteLocations } from "./routeLocations.js";
import { getSanitationForm } from "./sanitationForms.js";
import { getFarmIdFromUrl } from "../utils.js";
import { DebugLogger } from "./debugLogger.js";
import {
  routeUrlForLocalRoute,
  localRouteIdFromHash,
  localRouteIdFromLocation,
  sameDocumentPath,
} from "../routeNavigation.js";

export async function initializeSanitationRoute({
  lot,
  monitorId,
  monitorName,
  sanitationForm,
}) {
  // Calculate total trees in route based on lot tree percentage and total trees in the lot
  const totalTreesInRoute = sanitationForm.open_ended
    ? Infinity
    : Math.ceil((sanitationForm.lot_tree_percentage / 100) * lot.total_trees);

  // Insert a new sanitation route with the calculated total trees and form
  // reference. It is stamped as the most recently active route on creation, so
  // it becomes the active route without touching any other route.
  return await addSanitationRoute({
    monitorId,
    monitorName,
    lotId: lot.id,
    lotName: lot.name,
    farmId: sanitationForm.farm_id,
    sanitationFormId: sanitationForm.id,
    sanitationFormName: sanitationForm.name,
    routeQuestionId: sanitationForm.sanitation_route_question_id,
    treeQuestionId: sanitationForm.sanitation_tree_question_id,
    treeSampleQuestionId: sanitationForm.sanitation_tree_sample_question_id,
    numberOfTreeSections: sanitationForm.number_of_tree_sections,
    samplesPerTreeSection: sanitationForm.samples_per_tree_section,
    hasRouteQuestion: sanitationForm.has_route_question,
    hasTreeQuestions: sanitationForm.has_tree_questions,
    hasTreeSampleQuestions: sanitationForm.has_tree_sample_questions,
    currentQuestionType: null,
    currentTreeNumber: 0,

    totalTreesInRoute: totalTreesInRoute,
  });
}

export async function getCurrentDatapointUrl(sanitationRouteId) {
  const route = await db.sanitationRoutes.get(sanitationRouteId);

  if (!route) {
    console.error(`Route with ID ${sanitationRouteId} not found.`);
    const farmId = getFarmIdFromUrl(window.location.pathname);
    return farmId ? `/farms/${farmId}/route_launcher` : "/";
  }

  return currentDatapointUrlForRoute(route);
}

export function currentDatapointUrlForRoute(route) {
  if (!route) return "/";

  const {
    id: sanitationRouteId,
    farm_id,
    sanitation_form_id,
    route_question_id,
    tree_question_id,
    tree_sample_question_id,
    current_question_type,
  } = route;

  if (!current_question_type || current_question_type === "completed") {
    if (current_question_type === "completed") {
      return routeUrlForLocalRoute(
        `/farms/${farm_id}/sanitation_forms/${sanitation_form_id}/last_step`,
        sanitationRouteId,
      );
    }
    console.warn(
      `getCurrentDatapointUrl called for route ${sanitationRouteId} with null question type.`,
    );
    return `/farms/${farm_id}/route_launcher`;
  }

  if (current_question_type === "open_ended_checkpoint") {
    return routeUrlForLocalRoute(
      `/farms/${farm_id}/sanitation_forms/${sanitation_form_id}/open_ended_checkpoint`,
      sanitationRouteId,
    );
  }
  if (current_question_type === "samples_checkpoint") {
    return routeUrlForLocalRoute(
      `/farms/${farm_id}/sanitation_forms/${sanitation_form_id}/open_ended_samples_checkpoint`,
      sanitationRouteId,
    );
  }

  const sanitation_form_question_id =
    current_question_type === "route"
      ? route_question_id
      : current_question_type === "tree"
        ? tree_question_id
        : tree_sample_question_id;

  if (!sanitation_form_question_id) {
    console.error(
      `Could not determine question ID for current_question_type: ${current_question_type} on route ${sanitationRouteId}`,
    );
    return `/farms/${farm_id}/route_launcher`;
  }

  return routeUrlForLocalRoute(
    `/farms/${farm_id}/sanitation_forms/${sanitation_form_id}/sanitation_form_questions/${sanitation_form_question_id}/route_datapoint/new`,
    sanitationRouteId,
  );
}

export async function getNextDatapointUrl(
  sanitationRouteId,
  { intent = "next_step" } = {},
) {
  const route = await db.sanitationRoutes.get(sanitationRouteId);
  if (!route) {
    console.error(
      `Route with ID ${sanitationRouteId} not found for getNextDatapointUrl.`,
    );
    throw new Error("Recorrido no encontrado localmente.");
  }

  const sanitationForm = await getSanitationForm(route.sanitation_form_id);
  if (!sanitationForm) {
    console.error(
      `Sanitation form with ID ${route.sanitation_form_id} not found locally.`,
    );
    throw new Error(
      "Formulario de sanidad no encontrado localmente. Por favor, descargue los formularios desde el lanzador de rutas.",
    );
  }

  const now = new Date().toISOString();
  const { nextUrl, updateData } = calculateNextDatapointTransition(route, {
    sanitationForm,
    intent,
    completedAt: now,
  });

  await db.sanitationRoutes.update(sanitationRouteId, {
    ...updateData,
    last_active_at: now,
  });

  return routeUrlForLocalRoute(nextUrl, sanitationRouteId);
}

export function calculateNextDatapointTransition(
  route,
  {
    sanitationForm,
    intent = "next_step",
    completedAt = new Date().toISOString(),
  } = {},
) {
  const {
    farm_id,
    sanitation_form_id,
    route_question_id,
    tree_question_id,
    tree_sample_question_id,
    samples_per_tree_section,
    number_of_tree_sections,
    current_question_type,
    current_section_number,
    current_sample_number,
    current_tree_number,
    total_trees_in_route,
    has_tree_questions,
    has_tree_sample_questions,
    has_route_question,
  } = route;

  const { open_ended, open_ended_samples } = sanitationForm || {};
  let nextQuestionType = current_question_type;
  let nextSectionNumber = current_section_number;
  let nextSampleNumber = current_sample_number;
  let nextTreeNumber = current_tree_number;
  let nextUrl = "";

  if (current_question_type === null) {
    nextQuestionType = has_route_question
      ? "route"
      : has_tree_questions
        ? "tree"
        : "tree_sample";
    if (nextQuestionType === "tree" || nextQuestionType === "tree_sample") {
      nextTreeNumber = 1;
      nextSectionNumber = 1;
      nextSampleNumber = 1;
    } else {
      nextSectionNumber = null;
      nextSampleNumber = null;
      nextTreeNumber = null;
    }
  } else if (current_question_type === "route") {
    if (has_tree_questions) {
      nextQuestionType = "tree";
      nextTreeNumber = 1;
      nextSectionNumber = 1;
      nextSampleNumber = 1;
    } else if (has_tree_sample_questions) {
      nextQuestionType = "tree_sample";
      nextTreeNumber = 1;
      nextSectionNumber = 1;
      nextSampleNumber = 1;
    } else {
      nextQuestionType = "completed";
    }
  } else if (current_question_type === "tree") {
    if (has_tree_sample_questions) {
      nextQuestionType = "tree_sample";
      nextSectionNumber = 1;
      nextSampleNumber = 1;
    } else {
      nextTreeNumber++;
      if (nextTreeNumber > total_trees_in_route) {
        nextQuestionType = "completed";
      } else if (open_ended) {
        nextQuestionType = "open_ended_checkpoint";
      } else if (has_tree_questions) {
        nextQuestionType = "tree";
        nextSectionNumber = 1;
        nextSampleNumber = 1;
      } else {
        console.warn(
          "Reached end of tree questions unexpectedly in non-open-ended route.",
        );
        nextQuestionType = "completed";
      }
    }
  } else if (current_question_type === "tree_sample") {
    if (open_ended_samples) {
      nextQuestionType = "samples_checkpoint";
    } else {
      nextSampleNumber++;
      if (nextSampleNumber > samples_per_tree_section) {
        nextSampleNumber = 1;
        nextSectionNumber++;
        if (nextSectionNumber > number_of_tree_sections) {
          nextSectionNumber = 1;
          nextSampleNumber = 1;
          nextTreeNumber++;
          if (nextTreeNumber > total_trees_in_route) {
            nextQuestionType = "completed";
          } else if (open_ended) {
            nextQuestionType = "open_ended_checkpoint";
          } else if (has_tree_questions) {
            nextQuestionType = "tree";
          } else if (has_tree_sample_questions) {
            nextQuestionType = "tree_sample";
          } else {
            nextQuestionType = "completed";
          }
        } else {
          nextQuestionType = "tree_sample";
        }
      } else {
        nextQuestionType = "tree_sample";
      }
    }
  } else if (current_question_type === "samples_checkpoint") {
    if (intent === "continue_sample") {
      if (open_ended_samples) {
        nextQuestionType = "tree_sample";
        nextSampleNumber = current_sample_number + 1;
        nextSectionNumber = current_section_number;
      } else {
        let _potentialNextSample = current_sample_number + 1;
        let _potentialNextSection = current_section_number;

        if (_potentialNextSample > samples_per_tree_section) {
          _potentialNextSample = 1;
          _potentialNextSection = current_section_number + 1;
        }

        if (_potentialNextSection > number_of_tree_sections) {
          nextTreeNumber++;
          nextSectionNumber = 1;
          nextSampleNumber = 1;
          if (nextTreeNumber > total_trees_in_route) {
            nextQuestionType = "completed";
          } else if (open_ended) {
            nextQuestionType = "open_ended_checkpoint";
          } else if (has_tree_questions) {
            nextQuestionType = "tree";
          } else if (has_tree_sample_questions) {
            nextQuestionType = "tree_sample";
          } else {
            nextQuestionType = "completed";
          }
        } else {
          nextQuestionType = "tree_sample";
          nextSampleNumber = _potentialNextSample;
          nextSectionNumber = _potentialNextSection;
        }
      }
    } else if (intent === "complete_section") {
      let _targetNextSection = current_section_number + 1;

      if (_targetNextSection > number_of_tree_sections) {
        nextTreeNumber++;
        nextSectionNumber = 1;
        nextSampleNumber = 1;
        if (nextTreeNumber > total_trees_in_route) {
          nextQuestionType = "completed";
        } else if (open_ended) {
          nextQuestionType = "open_ended_checkpoint";
        } else if (has_tree_questions) {
          nextQuestionType = "tree";
        } else if (has_tree_sample_questions) {
          nextQuestionType = "tree_sample";
        } else {
          nextQuestionType = "completed";
        }
      } else {
        nextQuestionType = "tree_sample";
        nextSectionNumber = _targetNextSection;
        nextSampleNumber = 1;
      }
    } else if (intent === "complete_tree" || intent === "next_step") {
      nextTreeNumber++;
      nextSectionNumber = 1;
      nextSampleNumber = 1;

      if (nextTreeNumber > total_trees_in_route) {
        nextQuestionType = "completed";
      } else if (open_ended) {
        nextQuestionType = "open_ended_checkpoint";
      } else if (has_tree_questions) {
        nextQuestionType = "tree";
      } else if (has_tree_sample_questions) {
        nextQuestionType = "tree_sample";
      } else {
        nextQuestionType = "completed";
      }
    } else {
      nextQuestionType = "error";
    }
  } else if (current_question_type === "open_ended_checkpoint") {
    if (intent === "complete_route") {
      nextQuestionType = "completed";
    } else if (intent === "next_step") {
      if (has_tree_questions) {
        nextQuestionType = "tree";
        nextSectionNumber = 1;
        nextSampleNumber = 1;
      } else if (has_tree_sample_questions) {
        nextQuestionType = "tree_sample";
        nextSectionNumber = 1;
        nextSampleNumber = 1;
      } else {
        nextQuestionType = "completed";
      }
    } else {
      nextQuestionType = "error";
    }
  }

  let sanitation_form_question_id;
  if (nextQuestionType === "route") {
    sanitation_form_question_id = route_question_id;
  } else if (nextQuestionType === "tree") {
    sanitation_form_question_id = tree_question_id;
  } else if (nextQuestionType === "tree_sample") {
    sanitation_form_question_id = tree_sample_question_id;
  }

  if (nextQuestionType === "completed") {
    nextUrl = `/farms/${farm_id}/sanitation_forms/${sanitation_form_id}/last_step`;
  } else if (nextQuestionType === "open_ended_checkpoint") {
    nextUrl = `/farms/${farm_id}/sanitation_forms/${sanitation_form_id}/open_ended_checkpoint`;
  } else if (nextQuestionType === "samples_checkpoint") {
    nextUrl = `/farms/${farm_id}/sanitation_forms/${sanitation_form_id}/open_ended_samples_checkpoint`;
  } else if (sanitation_form_question_id) {
    nextUrl = `/farms/${farm_id}/sanitation_forms/${sanitation_form_id}/sanitation_form_questions/${sanitation_form_question_id}/route_datapoint/new`;
  } else {
    console.error(
      "Could not determine next step URL for route:",
      route,
      "Next state:",
      nextQuestionType,
    );
    nextUrl = `/farms/${farm_id}/route_launcher`;
    nextQuestionType = "error";
  }

  // 1. Start with data based on calculated next state (for standard progression)
  let updateData = {
    current_question_type: nextQuestionType,
    current_section_number: nextSectionNumber,
    current_sample_number: nextSampleNumber,
    current_tree_number: nextTreeNumber,
    status: "in_progress", // Default status
    completed_at: null, // Default completed_at
  };

  // 2. Override specific fields based on the *final* calculated nextQuestionType
  if (nextQuestionType === "completed" || nextQuestionType === "error") {
    // Update for terminal states (completed or error)
    updateData = {
      ...updateData, // Keep the calculated nextQuestionType
      current_section_number: null,
      current_sample_number: null,
      current_tree_number: null, // Nullify tree number on completion/error
      status: nextQuestionType === "completed" ? "completed" : "error",
      completed_at: completedAt, // Set completion timestamp (assuming field exists in schema)
    };
  } else if (nextQuestionType === "open_ended_checkpoint") {
    // Update for open_ended_checkpoint state
    updateData = {
      ...updateData, // Keep calculated nextQuestionType and nextTreeNumber
      current_section_number: null, // Nullify section/sample for checkpoint
      current_sample_number: null,
      completed_at: null, // Ensure completed_at is null
    };
  } else if (nextQuestionType === "samples_checkpoint") {
    // Update for samples_checkpoint state
    updateData = {
      ...updateData, // Keep calculated nextQuestionType
      // Preserve state *before* entering checkpoint
      current_section_number: current_section_number,
      current_sample_number: current_sample_number,
      current_tree_number: current_tree_number,
      completed_at: null, // Ensure completed_at is null
    };
  } else {
    // For standard progression ('route', 'tree', 'tree_sample'),
    // ensure completed_at is null if it might have been set previously.
    // The default updateData object already handles this.
    updateData.completed_at = null;
  }

  return { nextUrl, updateData, nextQuestionType };
}

export async function addSanitationRoute({
  farmId,
  lotId,
  lotName,
  monitorId,
  monitorName,
  sanitationFormId,
  sanitationFormName,
  routeQuestionId,
  treeQuestionId,
  treeSampleQuestionId,
  numberOfTreeSections,
  samplesPerTreeSection,
  hasRouteQuestion,
  hasTreeQuestions,
  hasTreeSampleQuestions,
  currentQuestionType,
  currentTreeNumber,
  totalTreesInRoute,
}) {
  const currentTimestamp = new Date().toISOString();

  const routeData = {
    last_active_at: currentTimestamp,
    status: "in_progress",
    farm_id: farmId,
    lot_id: lotId,
    lot_name: lotName,
    sanitation_monitor_id: monitorId,
    sanitation_monitor_name: monitorName,
    sanitation_form_id: sanitationFormId,
    sanitation_form_name: sanitationFormName,
    route_question_id: routeQuestionId,
    tree_question_id: treeQuestionId,
    tree_sample_question_id: treeSampleQuestionId,
    number_of_tree_sections: numberOfTreeSections,
    samples_per_tree_section: samplesPerTreeSection,
    has_route_question: hasRouteQuestion,
    has_tree_questions: hasTreeQuestions,
    has_tree_sample_questions: hasTreeSampleQuestions,
    current_question_type: currentQuestionType,
    current_section_number: 1,
    current_sample_number: 1,
    current_tree_number: currentTreeNumber,
    total_trees_in_route: totalTreesInRoute,
    created_at: currentTimestamp,
    completed_at: null,
  };

  if (!("completed_at" in db.sanitationRoutes.schema.idxByName)) {
    delete routeData.completed_at;
  }

  Object.keys(routeData).forEach(
    (key) => routeData[key] === undefined && delete routeData[key],
  );

  const routeId = await db.sanitationRoutes.add(routeData);
  DebugLogger.info("Local sanitation route created", {
    routeId,
    farmId,
    lotId,
    sanitationFormId,
    status: routeData.status,
  });

  return routeId;
}

export async function getSanitationRoute(sanitationRouteId) {
  return await db.sanitationRoutes.get(sanitationRouteId);
}

export function expectedSanitationFormQuestionId(route) {
  if (!route) return null;

  switch (route.current_question_type) {
    case "route":
      return route.route_question_id ?? null;
    case "tree":
      return route.tree_question_id ?? null;
    case "tree_sample":
      return route.tree_sample_question_id ?? null;
    default:
      return null;
  }
}

const DATAPOINT_QUESTION_TYPES = new Set(["route", "tree", "tree_sample"]);

export async function resolveBoundSanitationRouteForDocument({
  href = typeof window !== "undefined" ? window.location.href : "",
  pageFarmId = null,
  pageSanitationFormId = null,
  pageQuestionId = null,
  pageQuestionType = null,
  allowedStatuses = ["in_progress"],
} = {}) {
  const parsedHref = new URL(href, "http://local.invalid");
  const routeId = localRouteIdFromHash(parsedHref.hash);

  if (!routeId) {
    return {
      ok: false,
      reason: "missing_binding",
      route: null,
      recoveryUrl: null,
    };
  }

  const route = await getSanitationRoute(routeId);
  if (!route) {
    return {
      ok: false,
      reason: "route_not_found",
      route: null,
      recoveryUrl: null,
    };
  }

  const recoveryUrl = currentDatapointUrlForRoute(route);

  if (pageFarmId != null && Number(route.farm_id) !== Number(pageFarmId)) {
    return { ok: false, reason: "farm_mismatch", route, recoveryUrl };
  }

  if (
    pageSanitationFormId != null &&
    Number(route.sanitation_form_id) !== Number(pageSanitationFormId)
  ) {
    return { ok: false, reason: "form_mismatch", route, recoveryUrl };
  }

  if (!allowedStatuses.includes(route.status)) {
    return { ok: false, reason: "status_mismatch", route, recoveryUrl };
  }

  if (
    pageQuestionType != null &&
    route.current_question_type !== pageQuestionType
  ) {
    return { ok: false, reason: "question_type_mismatch", route, recoveryUrl };
  }

  if (
    pageQuestionId != null &&
    DATAPOINT_QUESTION_TYPES.has(route.current_question_type)
  ) {
    const expectedQuestionId = expectedSanitationFormQuestionId(route);
    if (
      expectedQuestionId == null ||
      Number(expectedQuestionId) !== Number(pageQuestionId)
    ) {
      return { ok: false, reason: "question_id_mismatch", route, recoveryUrl };
    }
  }

  if (
    !recoveryUrl ||
    String(recoveryUrl).includes("/route_launcher") ||
    !sameDocumentPath(parsedHref.href, recoveryUrl, parsedHref.origin)
  ) {
    return { ok: false, reason: "path_mismatch", route, recoveryUrl };
  }

  return { ok: true, reason: null, route, recoveryUrl };
}

export function resolveBoundRouteForController(controller, pageQuestionType) {
  return resolveBoundSanitationRouteForDocument({
    pageFarmId: controller.hasFarmIdValue ? controller.farmIdValue : null,
    pageSanitationFormId: controller.hasSanitationFormIdValue
      ? controller.sanitationFormIdValue
      : null,
    pageQuestionType,
  });
}

export async function getSanitationRouteBoundToDocument({
  explicitRouteId = null,
} = {}) {
  if (explicitRouteId != null) {
    const routeId = Number(explicitRouteId);
    if (!Number.isInteger(routeId) || routeId <= 0) return null;
    return getSanitationRoute(routeId);
  }

  const boundRouteId = localRouteIdFromLocation();
  if (!boundRouteId) return null;
  return getSanitationRoute(boundRouteId);
}

const ACTIVE_ROUTE_STATUSES = new Set(["in_progress", "completed"]);

function resolvedFarmId(farmId = null) {
  const candidateFarmId = farmId ?? getFarmIdFromUrl(window.location.pathname);

  return candidateFarmId == null ? null : Number(candidateFarmId);
}

function sameFarm(route, farmId) {
  return farmId != null && Number(route.farm_id) === farmId;
}

// The active route is derived, not stored: it is the current farm's
// in_progress/completed route the monitor most recently worked on.
// `last_active_at` is stamped on create, resume, and every advance, so there is
// no flag to lose (a paused route that is re-entered via back/reload/deep-link
// still resolves here). Legacy rows created before `last_active_at` existed
// fall back to `created_at`.
// Rank tuple for "most recently active" selection, compared element by element
// (highest wins). The tier keeps legacy rows correct across the upgrade to
// last_active_at:
//   2 → has last_active_at (this model's recency signal)
//   1 → only the legacy `active` flag, set before last_active_at existed (e.g. a
//        resumed older route whose created_at was never bumped) — this must beat
//        a newer-created but inactive route
//   0 → neither; fall back to created_at
// Legacy rows age out: the next advance or resume stamps last_active_at and
// promotes the route to tier 2. Ties break toward the higher id (created last).
function routeRank(route) {
  const tier = route.last_active_at ? 2 : route.active === true ? 1 : 0;
  const recency = route.last_active_at || route.created_at || "";
  return [tier, recency, route.id];
}

function outranks(a, b) {
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return a[i] > b[i];
  }
  return false;
}

// Pure selection over a set of routes; shared with the debug panel so its
// "active" display matches getActiveSanitationRoute exactly.
export function activeRouteFrom(routes, farmId = null) {
  const currentFarmId = farmId == null ? null : Number(farmId);
  const candidates = routes.filter(
    (route) =>
      ACTIVE_ROUTE_STATUSES.has(route.status) &&
      (currentFarmId == null || Number(route.farm_id) === currentFarmId),
  );
  if (candidates.length === 0) return undefined;

  return candidates.reduce((latest, route) =>
    outranks(routeRank(route), routeRank(latest)) ? route : latest,
  );
}

export async function getActiveSanitationRoute(farmId = null) {
  const currentFarmId = resolvedFarmId(farmId);
  return activeRouteFrom(await db.sanitationRoutes.toArray(), currentFarmId);
}

export async function markSanitationRouteAsActive(sanitationRouteId) {
  return await db.sanitationRoutes.update(sanitationRouteId, {
    last_active_at: new Date().toISOString(),
  });
}

export async function getInProgressSanitationRoutes(farmId = null) {
  const currentFarmId = resolvedFarmId(farmId);

  return await db.sanitationRoutes
    .filter(
      (route) =>
        route.status === "in_progress" && sameFarm(route, currentFarmId),
    )
    .toArray();
}

export async function getCompletedSanitationRoutes(farmId = null) {
  const currentFarmId = resolvedFarmId(farmId);

  return await db.sanitationRoutes
    .filter(
      (route) => route.status === "completed" && sameFarm(route, currentFarmId),
    )
    .toArray();
}

export async function getVisibleSanitationRoutesForOtherFarms(farmId = null) {
  const currentFarmId = resolvedFarmId(farmId);
  if (currentFarmId == null) return [];

  return await db.sanitationRoutes
    .filter(
      (route) =>
        route.farm_id != null &&
        ACTIVE_ROUTE_STATUSES.has(route.status) &&
        !sameFarm(route, currentFarmId),
    )
    .toArray();
}

export async function deleteSanitationRoute(sanitationRouteId) {
  const routeDatapoints = await getRouteDatapoints(sanitationRouteId);
  const routeLocations = await getRouteLocations(sanitationRouteId);
  const route = await db.sanitationRoutes.get(sanitationRouteId);

  DebugLogger.warn("Local sanitation route delete requested", {
    routeId: sanitationRouteId,
    status: route?.status,
    lastActiveAt: route?.last_active_at,
    datapointCount: routeDatapoints.length,
    locationCount: routeLocations.length,
  });

  for (const datapoint of routeDatapoints) {
    await deleteRouteDatapoint(datapoint.id);
  }

  for (const location of routeLocations) {
    await deleteRouteLocation(location.id);
  }

  return await db.sanitationRoutes.delete(sanitationRouteId);
}
